import { useMemo, useState } from 'react';
import { BacklogTask } from '../domain/types';
import { nameByEmail } from '../domain/util';

export interface AjusteSuggestion {
  id: string;
  title: string;
  meta: string;
}

const MAX_SUGGESTIONS = 8;

const norm = (s: unknown) => String(s || '').toLowerCase().trim();

function compact(parts: Array<string | undefined | null>): string {
  return parts.map((p) => String(p || '').trim()).filter(Boolean).join(' · ');
}

export function formatAjusteSuggestion(t: BacklogTask, users: { email: string; first_name?: string; last_name?: string }[] = []): AjusteSuggestion {
  const description = t.description.trim() || 'Sin descripción';
  return {
    id: t.id,
    title: description.length > 78 ? `${description.slice(0, 78)}…` : description,
    meta: compact([
      t.proyecto || t.categoria || undefined,
      t.espacio || t.cliente_interno || undefined,
      nameByEmail(t.assigned_to, users as any),
      t.deadline ? `Entrega ${String(t.deadline).slice(0, 10)}` : undefined,
    ]),
  };
}

export function filterAjusteSuggestions(
  tasks: BacklogTask[],
  query: string,
  currentTaskId = '',
  users: { email: string; first_name?: string; last_name?: string }[] = []
): AjusteSuggestion[] {
  const q = norm(query);
  if (!q) return [];

  return tasks
    .filter((t) => String(t.id) !== String(currentTaskId))
    .map((t) => {
      const haystack = [
        t.id,
        t.description,
        t.proyecto,
        t.assigned_to,
        nameByEmail(t.assigned_to, users as any),
        t.espacio,
        t.cliente_interno,
        t.categoria,
        t.pieza,
      ].map(norm).join(' ');
      return { task: t, haystack, startsWithId: norm(t.id).startsWith(q), startsWithDescription: norm(t.description).startsWith(q) };
    })
    .filter(({ haystack }) => haystack.includes(q))
    .sort((a, b) => Number(b.startsWithId || b.startsWithDescription) - Number(a.startsWithId || a.startsWithDescription))
    .slice(0, MAX_SUGGESTIONS)
    .map(({ task }) => formatAjusteSuggestion(task, users));
}

export default function AjusteInput({
  value,
  onChange,
  tasks,
  currentTaskId,
  users,
  placeholder = 'Escribí una nota o buscá una tarea…',
}: {
  value: string;
  onChange: (value: string) => void;
  tasks: BacklogTask[];
  currentTaskId?: string;
  users?: { email: string; first_name?: string; last_name?: string }[];
  placeholder?: string;
}) {
  const [focused, setFocused] = useState(false);
  const [picked, setPicked] = useState(false);
  const suggestions = useMemo(
    () => filterAjusteSuggestions(tasks, value, currentTaskId, users || []),
    [tasks, value, currentTaskId, users]
  );
  const showSuggestions = focused && !picked && suggestions.length > 0;
  const selected = tasks.find((t) => t.id === value && t.id !== currentTaskId);

  return (
    <div className="ajuste-input">
      <input
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={() => { setFocused(true); setPicked(false); }}
        onBlur={() => window.setTimeout(() => setFocused(false), 120)}
        onChange={(e) => { setPicked(false); onChange(e.target.value); }}
      />
      {selected && (
        <div className="hint ajuste-selected">
          Referencia elegida: {formatAjusteSuggestion(selected, users || []).title}
        </div>
      )}
      {showSuggestions && (
        <div className="ajuste-suggestions" role="listbox" aria-label="Sugerencias de tareas para ajuste">
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              className="ajuste-suggestion"
              role="option"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setPicked(true); onChange(s.id); setFocused(false); }}
            >
              <span>{s.title}</span>
              <small>{s.meta || s.id}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
